import { MouseEvent, useState } from "react";
import { Box, Button, Divider, IconButton, ListItemIcon, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import { useMenu } from "path-to-your-hooks"; // adjust this import
// import { professionalTheme } from "./path-to-theme"; // reuse your existing theme
import useMenu from 'components/services/Menu';
import { Logo } from "./Logo";
import { useNavigate } from "react-router-dom";
import { iconFromName } from "./ui/DynamicIcon";
import { useRecoilState, useRecoilValue } from "recoil";
import { colorsAtom, currentUserSessionAtom } from "library/store";
import { isFalsy } from 'utility-types';

const Sidebar = () => {
    const navigate = useNavigate()
    const [menuClickCount, setMenuClickCount] = useState<number>(-1);
    const { applicationMenus: menus, setApplicationMenus: setMenus } = useMenu();
    const { applicationSetup } = useRecoilValue(currentUserSessionAtom);
    const [Colors, setColors] = useRecoilState(colorsAtom);

    const menuClick = (event: MouseEvent<HTMLButtonElement>, name: string): void => {
        event.preventDefault();
        const updatedMenus = menus.map((menu) =>
            menu.name === name ? { ...menu, isOpen: !menu.isOpen } : menu
        );
        setMenus(updatedMenus);
    };

    const closeMenu = (name: string): void => {

        const arr = [...menus];
        const menu = arr.find(m => m.name === name);
        if (!menu) return;

        setMenuClickCount(menuClickCount - 1);
        menu.isOpen = false;
        setMenus([...arr]);
    }


    const menuItemClick = (event: MouseEvent<HTMLButtonElement>, name: string, routeToGo: string): void => {
        event.preventDefault();

        closeMenu(name);
        navigate(`${routeToGo}/${menuClickCount}`);
    }

    const professionalTheme = {

        sidebarBackground: Colors.primary,

        activeBackground: "#3b82f6",

        // Hover states
        hoverBackground: Colors.primary, // slate-700

        // Text colors
        primaryText: "#ffffff",
        secondaryText: "#cbd5e1", // slate-300

        // Logo gradient
        logoGradientFrom: "#4f46e5", // indigo-600
        logoGradientTo: "#7c3aed", // violet-600

        // Children/submenu background
        submenuBackground: "#0f172a", // slate-900
    };
    return (
        <Box
            sx={{
                background: Colors.primary,
                borderRight: "1px solid #334155",
                display: "flex",
                flexDirection: "column",
                maxHeight: "100vh",
                overflowY: "auto",
                position: "relative",
                width: 280, // Adjust width as needed
                height: "100vh", // ensure full viewport height
                overflow: "hidden", // contain scrollable content
                //scrollbarWidth: "none", // Firefox
                //msOverflowStyle: "none", // Internet Explorer 10+
                //"&::-webkit-scrollbar": {
                //    display: "none", // Chrome, Safari, Edge
                //},
            }}
        >
            {/* Logo Section */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 3,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    borderBottom: "1px solid #334155",
                    bgcolor: professionalTheme.sidebarBackground,
                }}
            >
                <Logo />
                <Box>

                    <Typography
                        variant="h5"
                        sx={{
                            color: professionalTheme.primaryText,
                            fontWeight: "bold",
                            letterSpacing: -0.5,
                        }}
                    >
                        Kampux
                    </Typography>
                    <Typography fontSize="14px" sx={{
                        color: professionalTheme.primaryText
                    }}>
                        {applicationSetup.companyName}
                    </Typography>
                </Box>
            </Box>

            {/* Menus */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    scrollbarWidth: "thin", // for Firefox
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#475569', // customize scroll thumb
                        borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: Colors.primary,
                    },
                }}
            >

                {menus.map(({ name, text, anchorRef, iconName, isOpen, menuItems }, idx) => {
                    const icon = iconName
                    return (
                        <Box
                            key={"menus" + idx}
                            sx={{
                                bgcolor: isOpen
                                    ? professionalTheme.activeBackground
                                    : "transparent",
                                borderLeft: isOpen
                                    ? `4px solid #60a5fa`
                                    : "4px solid transparent",
                                "&:hover": {
                                    bgcolor: isOpen
                                        ? professionalTheme.activeBackground
                                        : professionalTheme.hoverBackground,
                                },

                                transition: "all 0.2s ease-in-out",
                            }}
                            onClick={(event: any) => menuClick(event, name)}
                        >
                            <Button
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    gap: 2,
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 3,
                                    py: 2,
                                }}
                            >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2
                                }}>

                                    {icon && <ListItemIcon color="primary">
                                        {iconFromName(icon, Colors.menuIcon)}

                                    </ListItemIcon>}
                                    <Typography
                                        sx={{
                                            color: (theme) => theme.palette.getContrastText(Colors.primary),
                                            fontWeight: 600,
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {text}
                                    </Typography>
                                </Box>

                                {menuItems?.length ? (
                                    <IconButton
                                        disableRipple
                                        size="small"
                                        aria-label="chevron Button"
                                        sx={{
                                            color: professionalTheme.primaryText,
                                            padding: "4px",
                                            "&:hover": {
                                                bgcolor: "rgba(255, 255, 255, 0.1)",
                                            },
                                            transform: isOpen ? "rotate(180deg)" : undefined,
                                            transition: "all .3s ease-in-out"
                                        }}

                                    >
                                        <KeyboardArrowDownIcon
                                            sx={{
                                                color: "white"
                                            }}
                                        />
                                    </IconButton>
                                ) : null}


                            </Button>

                            {/* Submenus */}
                            {isOpen && menuItems && (
                                <Box
                                    sx={{
                                        bgcolor: Colors.primary,
                                        display: "flex",
                                        flexDirection: "column",
                                        px: 4,
                                        py: 1,
                                        overflow: "hidden",
                                        maxHeight: isOpen && menuItems ? "100vh" : "0",
                                        transition: "all 0.3s ease-in-out",
                                    }}
                                >
                                    {menuItems.map(({ routeToGo, text, group, entityName, iconName }, index, array) => {
                                        const icon = iconName || 'ai/AiOutlineArrowRight';
                                        return (
                                            <Box
                                                key={`child-${index}`}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    py: 1,
                                                    color: (theme) => theme.palette.getContrastText(Colors.primary),
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        color: "#fff",
                                                        bgcolor: Colors.primary,
                                                    },
                                                }}
                                                onClick={(event: any) => menuItemClick(event, name, routeToGo)}
                                            >
                                                {icon && <ListItemIcon color="primary">
                                                    {iconFromName(icon, Colors.menuIcon)}
                                                    {/* <DynamicIcon icon={icon} size="1em" color="primary.main"  /> */}
                                                </ListItemIcon>}
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        textTransform: "capitalize",
                                                    }}
                                                >
                                                    {text}
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            )}
                        </Box>
                    )
                })}
            </Box>
        </Box>
    );
};

export default Sidebar;
